import { Stack, Typography } from '@mui/material';
import { useEffect, useRef } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Trans } from 'react-i18next';

import { StyledSwitch } from '@/components/Form/Form.styled';
import { Divider } from '@/components/Layout/Divider';

import { useAppSelector } from '@/hooks/redux';
import { useAddressCityList } from '@/hooks/useAddressCityList';
import { useTranslate } from '@/hooks/useTranslate';

import { selectLoggedInUser } from '@/redux/auth/selectors';

import { Option } from '@/types/components/common';
import { MailingAddressSectionFormValues } from '@/types/form';

import { AddressCityField } from './AddressCityField';
import {
  AddressCountrySelectField,
  AddressLine1Field,
  AddressStateField,
  AddressZipField,
} from './StandardAddressFormFields';

type MailingAddressSectionProps = Readonly<{
  popularCountryOptions: Option[];
  isMinor?: boolean;
  isDisabled?: boolean;
}>;

const MAILING_ADDRESS_FIELDS = [
  'mailingLine1',
  'mailingCity',
  'mailingState',
  'mailingZip',
  'mailingCountry',
] as const;

export function MailingAddressSection({
  popularCountryOptions,
  isMinor = false,
  isDisabled = false,
}: MailingAddressSectionProps) {
  const { t } = useTranslate();
  const user = useAppSelector(selectLoggedInUser);

  const { control, setValue, clearErrors } =
    useFormContext<MailingAddressSectionFormValues>();
  const selectedCountry = useWatch({ control, name: 'mailingCountry' });
  const mailingLine1 = useWatch({ control, name: 'mailingLine1' });
  const mailingSameAsResidential = useWatch({
    control,
    name: 'mailingSameAsResidential',
  });

  const prevMailingSameAsResidentialRef = useRef<boolean | undefined>(
    undefined,
  );

  const { resolveCityFromPlace } = useAddressCityList();

  useEffect(() => {
    if (mailingLine1 && typeof mailingLine1 === 'object') {
      setValue(
        'mailingCity',
        resolveCityFromPlace(mailingLine1.city, mailingLine1.country),
      );
      setValue('mailingState', mailingLine1.state || '');
      setValue('mailingZip', mailingLine1.zip || '');
      setValue('mailingCountry', mailingLine1.country || '');
      setValue('mailingLine1', mailingLine1.line1 || '');
      clearErrors(MAILING_ADDRESS_FIELDS);
    }
  }, [mailingLine1, setValue, clearErrors, resolveCityFromPlace]);

  useEffect(() => {
    const prev = prevMailingSameAsResidentialRef.current;
    prevMailingSameAsResidentialRef.current = mailingSameAsResidential;

    if (prev === undefined) {
      return;
    }

    if (mailingSameAsResidential && prev === false) {
      MAILING_ADDRESS_FIELDS.forEach(field => setValue(field, ''));
      clearErrors(MAILING_ADDRESS_FIELDS);
    }
  }, [mailingSameAsResidential, setValue, clearErrors]);

  const onCountryChange = () => setValue('mailingCity', '');

  const showMailingFields = mailingSameAsResidential === false;

  return (
    <Stack gap={1} direction="column" width="100%">
      <Controller
        name="mailingSameAsResidential"
        control={control}
        render={({ field }) => (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
          >
            <Typography variant="body2" component="span">
              {t(
                isMinor
                  ? 'createNewAccount.addressPage.form.mailingSameAsResidential.minor.label'
                  : 'createNewAccount.addressPage.form.mailingSameAsResidential.label',
              )}
            </Typography>
            <StyledSwitch
              checked={field.value !== false}
              onChange={(_, checked) => field.onChange(checked)}
              onBlur={field.onBlur}
              disabled={isDisabled}
            />
          </Stack>
        )}
      />
      {showMailingFields && (
        <Stack gap={3} direction="column" width="100%">
          <Divider containerProps={{ mt: 1 }} width="50%" />
          <Typography variant="body2" textAlign="center">
            <Trans
              i18nKey={
                isMinor
                  ? 'createNewAccount.addressPage.form.mailingSection.minor.heading'
                  : 'createNewAccount.addressPage.form.mailingSection.heading'
              }
              components={{ bold: <strong /> }}
            />
          </Typography>
          <AddressLine1Field
            name="mailingLine1"
            control={control}
            isPending={isDisabled}
          />
          <AddressCityField
            name="mailingCity"
            control={control}
            isPending={isDisabled}
            userTerritoryCode={user?.territoryCode}
            selectedCountry={selectedCountry}
            textFieldPending={isDisabled}
          />
          <AddressStateField
            name="mailingState"
            control={control}
            isPending={isDisabled}
          />
          <AddressZipField
            name="mailingZip"
            control={control}
            isPending={isDisabled}
          />
          <AddressCountrySelectField
            name="mailingCountry"
            control={control}
            isPending={isDisabled}
            popularCountryOptions={popularCountryOptions}
            disabled={isDisabled}
            onSelectChange={onCountryChange}
          />
        </Stack>
      )}
    </Stack>
  );
}
